<template>
  <LoadingSpinner v-if="isLoading" />
  <div v-else class="show-vendor">
    <PageHeader
      :title="vendor.name"
      :action="{ title: 'Edit', to: { name: 'EditVendor', params: { id: vendor.id } } }"
    />

    <div class="vendor-details">
      <div class="vendor-header">
        <div class="vendor-logo">
          <BaseAvatar :src="vendor.logo" class="size-40" />
        </div>
        <div class="vendor-info">
          <h2 class="text-2xl font-semibold text-slate-950">{{ vendor.name }}</h2>
          <div class="contact-details">
            <div class="detail-item">
              <span class="text-slate-500">Email:</span>
              <span>{{ vendor.email }}</span>
            </div>
            <div class="detail-item">
              <span class="text-slate-500">Phone:</span>
              <span>{{ vendor.phone }}</span>
            </div>
            <div class="detail-item">
              <span class="text-slate-500">Address:</span>
              <span>{{ vendor.address }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="vendor-sections">
        <div class="section">
          <h3 class="text-xl font-semibold mb-4">Purchase Orders ({{ purchaseOrders?.length }})</h3>
          <BaseTable
            :items="purchaseOrders ?? []"
            :fields="purchaseOrderFields"
            :is-searchable="true"
            :clickable="true"
          >
            <template #status-data="{ row }">
              <span :class="['badge', row.status === 'open' ? 'badge-success' : 'badge-danger']">
                {{ row.status }}
              </span>
            </template>
            <template #date-data="{ row }">
              {{ new Date(row.date).toLocaleDateString() }}
            </template>
            <template #purchaseOrderId-data="{ row }">
              <router-link :to="{ name: 'ShowPurchaseOrder', params: { id: row.id } }">
                {{ row.purchaseOrderId }}
              </router-link>
            </template>
          </BaseTable>
        </div>

        <div class="section">
          <h3 class="text-xl font-semibold mb-4">Bills ({{ bills?.length }})</h3>
          <BaseTable
            :items="bills ?? []"
            :fields="billFields"
            :is-searchable="true"
            :clickable="true"
          >
            <template #status-data="{ row }">
              <span
:class="[
                'badge',
                row.status === 'outstanding' ? 'badge-warning' :
                row.status === 'paid' ? 'badge-success' :
                'badge-danger'
              ]">
                {{ row.status }}
              </span>
            </template>
            <template #dueDate-data="{ row }">
              {{ new Date(row.dueDate).toLocaleDateString() }}
            </template>
            <template #billNumber-data="{ row }">
              <router-link :to="{ name: 'ShowBill', params: { id: row.id } }">
                {{ row.billNumber }}
              </router-link>
            </template>
          </BaseTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useQuery } from '@tanstack/vue-query'
import { useRoute } from 'vue-router'
import VendorService from '@/services/VendorService'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import PageHeader from '@/components/app/AppPageHeader.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import BaseAvatar from '@/components/base/BaseAvatar.vue'

const vendorId = useRoute().params.id as string

const purchaseOrderFields = [
  { key: 'status', label: 'Status' },
  { key: 'date', label: 'Date' },
  { key: 'purchaseOrderId', label: 'PO#' }
]

const billFields = [
  { key: 'status', label: 'Status' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'billNumber', label: 'BILL#' }
]

const vendorService = new VendorService()

const { data: vendor, isLoading } = useQuery({
  queryKey: ['vendor', vendorId],
  queryFn: () => vendorService.find(vendorId)
})

const { data: bills } = useQuery({
  queryKey: ['bills', vendorId],
  queryFn: () => vendorService.getBills(vendorId),
  enabled: !!vendorId
})

const { data: purchaseOrders } = useQuery({
  queryKey: ['purchaseOrders', vendorId],
  queryFn: () => vendorService.getPurchaseOrders(vendorId),
  enabled: !!vendorId
})
</script>

<style lang="scss" scoped>
.show-vendor {
  .vendor-details {
    @apply space-y-8;
  }

  .vendor-header {
    @apply flex items-start gap-8 p-6 bg-white rounded-lg shadow-sm;
  }

  .vendor-logo {
    @apply flex-shrink-0;
  }

  .vendor-info {
    @apply flex-1;
  }

  .contact-details {
    @apply mt-4 space-y-2;
  }

  .detail-item {
    @apply flex gap-2;
  }

  .vendor-sections {
    @apply space-y-8;
  }

  .section {
    @apply bg-white p-6 rounded-lg shadow-sm;
  }

  .badge {
    @apply px-2 py-1 rounded text-sm font-medium;

    &-success {
      @apply bg-green-100 text-green-800;
    }

    &-danger {
      @apply bg-red-100 text-red-800;
    }

    &-warning {
      @apply bg-yellow-100 text-yellow-800;
    }
  }
}
</style>
